// covered-by: qa/rickrubin/tests/api/ask-puntt-share.spec.ts
// covered-by: frontends/carrie/src/utils/queries/__tests__/askPuntt.test.tsx
import {
  type AskPunttChatScope,
  type ListAskPunttChatsResponse,
  type GetAskPunttChatHistoryResponse,
  type CreateAskPunttChatResponse,
  type SendAskPunttMessageResponse,
  type DeleteAskPunttChatResponse,
  type AbortAskPunttMessageResponse,
  type AskPunttProjectData,
  type SearchEnterpriseUsersResponse,
  type ShareAskPunttChatResponse,
  type UnshareAskPunttChatResponse,
  type UpdateAskPunttChatResponse,
} from "@mg/schemas/src/christo/askPuntt";
import {
  type ContinueReviewAttentionResponse,
  type GetReviewAttentionResponse,
} from "@mg/schemas/src/christo/askPunttReviewAttention";
import {
  type DismissAskPunttHomeMentionTaskResponse,
  type MarkAskPunttArtifactSeenResponse,
  type MarkAskPunttChatReadResponse,
} from "@mg/schemas/src/christo/askPunttUserSeen";

import { getUsers } from "./users";

import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";

/**
 * Ask Puntt Service - API client for the Ask Puntt chat feature
 */

/**
 * christo bounds the free-text query params it accepts — `search` on the chats
 * list (`listAskPunttChatsRequestSchema`, `.max(200)`) and `q` on enterprise
 * user search (`searchEnterpriseUsersRequestSchema`, `.max(120)`). Exceeding
 * either makes `validateSchema` answer 400 for the WHOLE request, so one
 * over-long paste into the launcher search box turned the entire chats list
 * red (`AskPunttChatsView` renders `isError`) and one into the share /
 * @mention picker made the picker throw — neither is "no matches". Clamping
 * here sends a prefix, whose substring match is a superset of what the caller
 * asked for, so the surface degrades to an empty result instead of an error.
 */
const MAX_LIST_SEARCH_CHARS = 200;
const MAX_USER_SEARCH_CHARS = 120;

function clampQuery(raw: string | undefined, max: number): string {
  // Trim before slicing so surrounding whitespace does not eat the budget, and
  // again after in case the cut lands mid-gap and leaves a trailing space.
  return (raw ?? "").trim().slice(0, max).trim();
}

export async function listChats(params?: {
  limit?: number;
  offset?: number;
  scope?: AskPunttChatScope;
  search?: string;
  participantIds?: string[];
  regions?: string[];
  tags?: string[];
  sortBy?: "updatedAt" | "createdAt" | "name";
}): Promise<ListAskPunttChatsResponse> {
  const queryParams = new URLSearchParams();
  if (params?.limit != null) queryParams.set("limit", String(params.limit));
  if (params?.offset != null) queryParams.set("offset", String(params.offset));
  if (params?.scope) queryParams.set("scope", params.scope);
  const search = clampQuery(params?.search, MAX_LIST_SEARCH_CHARS);
  if (search) queryParams.set("search", search);
  for (const id of params?.participantIds ?? []) {
    if (id.trim()) queryParams.append("participantIds", id.trim());
  }
  if (params?.regions?.length)
    queryParams.set("regions", params.regions.join(","));
  if (params?.tags?.length) queryParams.set("tags", params.tags.join(","));
  if (
    params?.sortBy === "updatedAt" ||
    params?.sortBy === "createdAt" ||
    params?.sortBy === "name"
  ) {
    queryParams.set("sortBy", params.sortBy);
  }

  const queryString = queryParams.toString();
  const url = `${API_URL}/private/catalyst/ask-puntt/chats${queryString ? `?${queryString}` : ""}`;

  const json = await smartFetch(url, { method: "GET" });
  return json as ListAskPunttChatsResponse;
}

export async function getChatHistory(
  chatId: string,
): Promise<GetAskPunttChatHistoryResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}`,
    { method: "GET" },
  );
  return json as GetAskPunttChatHistoryResponse;
}

export async function getChatEnterpriseId(
  chatId: string,
): Promise<{ enterpriseId: string }> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/enterprise`,
    { method: "GET" },
  );
  return json as { enterpriseId: string };
}

export async function getReviewAttention(
  chatId: string,
): Promise<GetReviewAttentionResponse> {
  return (await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/review-attention`,
    { method: "GET" },
  )) as GetReviewAttentionResponse;
}

export async function continueReviewAttention(
  episodeId: string,
  idempotencyKey: string,
): Promise<ContinueReviewAttentionResponse> {
  return (await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/review-attention/${episodeId}/continue`,
    { method: "POST", body: JSON.stringify({ idempotencyKey }) },
  )) as ContinueReviewAttentionResponse;
}

export async function createChat(payload: {
  message: string;
  workflowType?: string;
  attachments?: { key: string; source: string; type: string; name?: string }[];
  enabledConnectors?: string[];
  // Review-engine picker for artwork review: "standard" -> v1, "deep" -> v2.
  // Consulted server-side only when v2 production routing is enabled.
  reviewMode?: "standard" | "deep";
  // Set when a workflow card is opened from the home page (no attachments yet):
  // the backend replies with the skill's canned greeting and skips the LLM.
  workflowGreeting?: boolean;
  // Project linkage (Ask Puntt Projects). When set, the new chat is created
  // inside a project. `projectChatKind` distinguishes the team-visible shared
  // chat from a member's private personal chat. Omitted → standalone chat
  // (legacy behavior, unchanged).
  projectId?: string;
  projectChatKind?: "shared" | "personal";
  // Which agent answers this chat, for its whole life. Omitted means rembrandt.
  agent?: "rembrandt" | "warhol";
}): Promise<CreateAskPunttChatResponse> {
  const json = await smartFetch(`${API_URL}/private/catalyst/ask-puntt/chats`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return json as CreateAskPunttChatResponse;
}

export async function getMessageStatus(
  chatId: string,
  messageId: string,
): Promise<import("@mg/schemas/src/christo/askPuntt").AskPunttMessage> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/status`,
    { method: "GET" },
  );
  return json as import("@mg/schemas/src/christo/askPuntt").AskPunttMessage;
}

export async function submitFeedback(
  chatId: string,
  messageId: string,
  feedback: "thumbs_up" | "thumbs_down",
): Promise<{ success: boolean }> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/feedback`,
    {
      method: "PATCH",
      body: JSON.stringify({ feedback }),
    },
  );
  return json as { success: boolean };
}

export async function deleteChat(
  chatId: string,
): Promise<DeleteAskPunttChatResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}`,
    { method: "DELETE" },
  );
  return json as DeleteAskPunttChatResponse;
}

export async function renameChat(
  chatId: string,
  title: string,
): Promise<UpdateAskPunttChatResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ title }),
    },
  );
  return json as UpdateAskPunttChatResponse;
}

export async function sendMessage(
  chatId: string,
  payload: {
    message: string;
    attachments?: {
      key: string;
      source: string;
      type: string;
      name?: string;
    }[];
    sectionContext?: { id: string; title: string };
    mentions?: string[];
    mentionEmails?: string[];
    // Review-engine picker for artwork review: "standard" -> v1, "deep" -> v2.
    // Only consulted server-side when v2 production routing is enabled.
    reviewMode?: "standard" | "deep";
  },
): Promise<SendAskPunttMessageResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return json as SendAskPunttMessageResponse;
}

export async function updateProjectAction(
  chatId: string,
  action: "override_stage" | "complete_project",
  stageName?: string,
): Promise<{ success: boolean; projectData: AskPunttProjectData }> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/project/action`,
    {
      method: "PATCH",
      body: JSON.stringify({ action, stageName }),
    },
  );
  return json as { success: boolean; projectData: AskPunttProjectData };
}

/**
 * Phase 5: Lightweight streaming state polling.
 * Returns only the fields needed during streaming instead of the full chat document.
 *
 * `null` for a 404, which is NOT an error on this route: christo answers 404
 * while the turn's snapshot has not been published yet, and a subscriber arms
 * its socket the moment the turn starts — before warhol's first publish lands.
 * Measured on the independent Story 4 gate-3 e2e (`gate3/REPORT.md`): one 404
 * ~2s after a send, the run's only console error, for the message the turn had
 * not written yet. Returning `null` keeps that read out of the polling
 * fallback's failure count (`useAskPunttStream`), which exists to back a
 * snapshot that never lands off and was otherwise being driven by a turn that
 * simply had not started.
 */
export async function getMessageStreamingState(
  chatId: string,
  messageId: string,
): Promise<{
  status: string;
  content: string;
  streamingContent: string | null;
  agentSteps: unknown[];
  searchProgress: unknown[];
  canvasContent: unknown | null;
  canvasLoading: boolean;
  canvasChangedSectionIds: string[];
  viewTicketId: string | null;
  paneLabels: { context?: string; artwork?: string } | null;
  suggestedResponses: string[];
  projectData: unknown | null;
  projectLoading: boolean;
  contentBlocks: unknown[] | null;
  seq?: number;
  updatedAt?: string;
} | null> {
  try {
    const json = await smartFetch(
      `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/stream`,
      { method: "GET" },
    );
    return json as Awaited<ReturnType<typeof getMessageStreamingState>>;
  } catch (error) {
    // `smartFetch` throws the `Response` itself on a non-2xx. Only 404 is
    // "not published yet"; everything else (401, 431, 5xx) is a real failure
    // and has to keep rejecting, or the fallback's backoff never engages.
    if (
      typeof error === "object" &&
      error !== null &&
      (error as Response).status === 404
    ) {
      return null;
    }
    throw error;
  }
}

export async function abortMessage(
  chatId: string,
  messageId: string,
): Promise<AbortAskPunttMessageResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/abort`,
    { method: "POST" },
  );
  return json as AbortAskPunttMessageResponse;
}

export async function markChatRead(
  chatId: string,
  payload?: { messageId?: string },
): Promise<MarkAskPunttChatReadResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/read`,
    {
      method: "POST",
      body: JSON.stringify(payload ?? {}),
    },
  );
  return json as MarkAskPunttChatReadResponse;
}

export async function dismissHomeMentionTask(
  chatId: string,
): Promise<DismissAskPunttHomeMentionTaskResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/home-tasks/dismiss`,
    {
      method: "POST",
      body: JSON.stringify({ chatId }),
    },
  );
  return json as DismissAskPunttHomeMentionTaskResponse;
}

export async function markArtifactSeen(payload: {
  kind: "canvas" | "review";
  messageId?: string;
  ticketId?: string;
  reviewId?: string;
}): Promise<MarkAskPunttArtifactSeenResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/artifacts/seen`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return json as MarkAskPunttArtifactSeenResponse;
}

export async function shareChat(
  chatId: string,
  payload: { userIds?: string[]; emails?: string[] },
): Promise<ShareAskPunttChatResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/share`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return json as ShareAskPunttChatResponse;
}

export async function unshareChat(
  chatId: string,
  userId: string,
): Promise<UnshareAskPunttChatResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/share/${userId}`,
    { method: "DELETE" },
  );
  return json as UnshareAskPunttChatResponse;
}

function rosterFromGetUsers(
  limit: number,
): Promise<SearchEnterpriseUsersResponse> {
  return getUsers().then((users) => ({
    users: users
      .slice()
      .sort((a, b) =>
        (a.name ?? a.email ?? "").localeCompare(b.name ?? b.email ?? ""),
      )
      .slice(0, limit)
      .map((u) => ({
        _id: u._id,
        ...(u.name != null ? { name: u.name } : {}),
        ...(u.email != null ? { email: u.email } : {}),
        ...(u.avatar !== undefined ? { avatar: u.avatar ?? null } : {}),
      })),
  }));
}

/**
 * Server-side enterprise user search by name/email substring. Used by the
 * Share dialog, @mention composer, and other "pick a user" surfaces.
 * Bare `@` (empty `q`) returns the first page of the enterprise roster.
 */
export async function searchEnterpriseUsers(
  q: string,
  limit?: number,
): Promise<SearchEnterpriseUsersResponse> {
  const trimmed = clampQuery(q, MAX_USER_SEARCH_CHARS);
  const cap = limit ?? 25;
  const queryParams = new URLSearchParams();
  queryParams.set("q", trimmed);
  queryParams.set("limit", String(cap));
  try {
    const json = await smartFetch(
      `${API_URL}/private/catalyst/users/search?${queryParams.toString()}`,
      { method: "GET" },
    );
    return json as SearchEnterpriseUsersResponse;
  } catch (err) {
    // Older christo builds rejected empty `q` with 400 before the mention
    // composer shipped. Fall back to the roster GET so bare `@` still works.
    if (trimmed.length === 0 && err instanceof Response && err.status === 400) {
      return rosterFromGetUsers(cap);
    }
    throw err;
  }
}
